import classnames from "classnames";
import React, { forwardRef, InputHTMLAttributes, useId, useState } from "react";
import { Button } from "../Button";
import { CloseCircleIcon } from "../Icons";
import "./input.scss";

enum InputSize {
    "small",
    "medium",
}
export interface TInput extends InputHTMLAttributes<HTMLInputElement> {
    // type?: "text" | "number" | "password";
    readonly isValid?: boolean;
    inputSize?: keyof typeof InputSize | null;
    disabled?: boolean;
    loading?: boolean | null;
    label?: string;
    description?: string;
    successMessage?: string;
    errorMessage?: string;
    labelClassName?: string;
    inputClassName?: string;
    messageClassName?: string;
    icon?: JSX.Element | null;
    onIconClick?: () => void;
    withDeleteBtn?: boolean;
    onDeleteBtnClick?: () => void;
    hiddenLabel?: boolean;
    onDarkBackground?: boolean;
    submitButtonLabel?: string;
    withSubmitButton?: boolean;
    onSubmitButtonPress?: () => void;
    showValidationMessageOnBlurOnly?: boolean;
    fullWidth?: boolean;
    showAsterisk?: boolean;
    allowCustomColors?: boolean;
    labelNoWrap?: boolean;
    floatingMessage?: boolean;
}

export const Input = forwardRef<HTMLInputElement, TInput>(
    (
        {
            type = "text",
            label,
            disabled,
            loading,
            isValid,
            description,
            successMessage,
            errorMessage,
            inputSize = null,
            labelClassName,
            inputClassName = "",
            messageClassName,
            icon,
            onIconClick,
            withDeleteBtn = false,
            onDeleteBtnClick,
            hiddenLabel,
            onDarkBackground = false,
            submitButtonLabel,
            withSubmitButton,
            onSubmitButtonPress,
            showValidationMessageOnBlurOnly = false,
            fullWidth = false,
            showAsterisk = false,
            allowCustomColors = false,
            labelNoWrap = false,
            floatingMessage = false,
            ...props
        }: TInput,
        ref
    ) => {
        const labelId = useId();
        const [inputHasFocus, setInputHasFocus] = useState(false);
        const showValidationMessage = showValidationMessageOnBlurOnly ? !inputHasFocus : true;
        const ariaLabel = props["aria-label"];

        const MessageComponent = () => {
            const className = classnames(messageClassName, "c-input__message", {
                "c-input__message--floating": floatingMessage,
            });
            if (isValid === true && successMessage && showValidationMessage) {
                return (
                    <p
                        role="status"
                        aria-live="polite"
                        className={`${className} ${classnames("c-input__message--success", {
                            "c-input__message--success--on-dark-bg": onDarkBackground,
                        })}`}
                    >
                        {successMessage}
                    </p>
                );
            } else if (isValid === false && errorMessage && showValidationMessage) {
                return (
                    <p
                        role="alert"
                        aria-live="assertive"
                        className={`${className} ${classnames("c-input__message--error", {
                            "c-input__message--error--on-dark-bg": onDarkBackground,
                        })}`}
                    >
                        {errorMessage}
                    </p>
                );
            } else if (description) {
                return <p className={`${className}`}>{description}</p>;
            }
            return null;
        };

        const classNameInput = classnames(
            `c-input__input ${inputClassName}`,
            MessageComponent() && "c-input__input--w-message",
            inputSize && `c-input__input--${inputSize}`,
            disabled && `c-input__input--disabled`,
            icon && "c-input__input--w-icon",
            isValid !== undefined && (isValid ? `c-input__input--success` : `c-input__input--error`),
            withSubmitButton && "c-input__input--with-submit-button",
            {
                loading,
            }
        );

        const classNameComponent = classnames("c-input", {
            "c-input--full-width": fullWidth,
            "c-input--floating-message": floatingMessage,
        });
        const classNameLabel = classnames(labelClassName, "c-input__label", {
            "c-input__label--hidden": hiddenLabel,
            "c-input__label--on-dark-bg": onDarkBackground,
            "c-input__label--custom-colors": allowCustomColors,
            "c-input__label--no-wrap": labelNoWrap,
        });
        const classNameInputFieldWrapper = classnames("c-input__field-wrapper");
        const classNameInputIcon = classnames(
            labelClassName,
            "c-input__field-icon",
            inputSize && `c-input__field-icon--${inputSize}`
        );
        const classNameInputsubmitButton = classnames(
            "c-input__submit-button",
            inputSize && `c-input__submit-button--${inputSize}`
        );

        return (
            <div className={classNameComponent}>
                {label && (
                    <label id={labelId} className={classNameLabel}>
                        {label} {showAsterisk && "*"}
                    </label>
                )}
                <div className={classNameInputFieldWrapper}>
                    {icon && (
                        <button className={classNameInputIcon} onClick={onIconClick}>
                            {icon}
                        </button>
                    )}
                    <input
                        ref={ref}
                        disabled={disabled}
                        className={classNameInput}
                        {...props}
                        onFocus={e => {
                            setInputHasFocus(true);
                            props.onFocus?.(e);
                        }}
                        onBlur={e => {
                            setInputHasFocus(false);
                            props.onBlur?.(e);
                        }}
                        type={type}
                        onKeyDown={e => {
                            if (e.key === "Enter" && withSubmitButton && onSubmitButtonPress) {
                                onSubmitButtonPress();
                            }
                        }}
                        required={showAsterisk}
                        aria-label={ariaLabel}
                        aria-labelledby={label ? labelId : undefined}
                        aria-errormessage={errorMessage}
                    />
                    {withDeleteBtn && type === "text" && (
                        <button className="c-input__field-icon c-input__field-icon--delete" onClick={onDeleteBtnClick}>
                            <CloseCircleIcon />
                        </button>
                    )}
                    {submitButtonLabel && withSubmitButton && (
                        <Button
                            type="secondary"
                            onClick={() => onSubmitButtonPress?.()}
                            buttonClassName={classNameInputsubmitButton}
                        >
                            {submitButtonLabel}
                        </Button>
                    )}
                </div>
                <MessageComponent />
            </div>
        );
    }
);

Input.displayName = "Input";
